/**
 * Smart PDF Export Hook
 *
 * Two-path logic:
 * 1. If a PDF export already exists, download the most recent one immediately via presigned URL.
 * 2. If no PDF exists, trigger generation, poll the report every 3s (up to 60s),
 *    and auto-download when a new export appears.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { surveyReportApi } from '../api/surveyReportApi';
import type { PdfExportData } from '../types';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 60000;

export type SmartPdfExportPhase = 'idle' | 'downloading' | 'generating' | 'polling' | 'error';

interface UseSmartPdfExportReturn {
  handleExport: () => Promise<void>;
  isExporting: boolean;
  phase: SmartPdfExportPhase;
  error: string | null;
}

/**
 * Triggers a file download using a hidden anchor element.
 * Unlike window.open, this works reliably from async contexts
 * without being blocked by popup blockers.
 */
function triggerDownload(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Opens a URL in a new browser tab so the user can print via the browser's
 * native PDF viewer (Ctrl+P / Cmd+P).
 */
function openForPrint(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export type PdfExportMode = 'download' | 'print';

export function useSmartPdfExport(
  reportId: string,
  pdfExports: PdfExportData[],
  mode: PdfExportMode = 'download'
): UseSmartPdfExportReturn {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<SmartPdfExportPhase>('idle');
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  // Guard against overlapping poll requests
  const isPollingInFlightRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  const clearPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const executeUrlAction = useCallback(
    (url: string): void => {
      if (mode === 'print') {
        openForPrint(url);
      } else {
        triggerDownload(url);
      }
    },
    [mode]
  );

  const downloadExport = useCallback(
    async (exportId: string) => {
      const disposition = mode === 'print' ? 'inline' : 'attachment';
      const response = await surveyReportApi.downloadPdfExport(reportId, exportId, disposition);
      const downloadUrl = response.data?.downloadUrl;

      if (!downloadUrl) {
        throw new Error('Download URL not available');
      }

      executeUrlAction(downloadUrl);
    },
    [reportId, mode, executeUrlAction]
  );

  const startPolling = useCallback(
    (initialExportCount: number) => {
      if (!isMountedRef.current) return;

      setPhase('polling');

      // Timeout after 60s
      pollTimeoutRef.current = setTimeout(() => {
        clearPolling();
        if (isMountedRef.current) {
          setPhase('error');
          setError('PDF generation timed out. Please try again.');
        }
      }, POLL_TIMEOUT_MS);

      // Poll every 3s using direct API calls (bypasses React Query cache)
      pollIntervalRef.current = setInterval(async () => {
        // Skip if a previous poll request is still in flight
        if (isPollingInFlightRef.current) return;

        isPollingInFlightRef.current = true;
        try {
          const response = await surveyReportApi.getSurveyReport(reportId);

          if (!isMountedRef.current) return;

          const currentExports = response.data?.pdfExports ?? [];
          if (currentExports.length > initialExportCount) {
            // New export appeared - download it
            clearPolling();

            // Invalidate the main report query so UI reflects new exports
            await queryClient.invalidateQueries({ queryKey: ['surveyReport', reportId] });

            const newestExport = getMostRecentExport(currentExports);
            if (newestExport) {
              setPhase('downloading');
              await downloadExport(newestExport.exportId);
            }

            if (isMountedRef.current) {
              setPhase('idle');
            }
          }
        } catch {
          // Individual poll failures are silently retried; timeout handles persistent issues
        } finally {
          isPollingInFlightRef.current = false;
        }
      }, POLL_INTERVAL_MS);
    },
    [reportId, queryClient, clearPolling, downloadExport]
  );

  const handleExport = useCallback(async () => {
    if (phase !== 'idle' && phase !== 'error') return;

    setError(null);

    try {
      if (pdfExports.length > 0) {
        // Path 1: Download existing most recent export
        setPhase('downloading');
        const mostRecent = getMostRecentExport(pdfExports);
        if (mostRecent) {
          await downloadExport(mostRecent.exportId);
        }
        if (isMountedRef.current) {
          setPhase('idle');
        }
      } else {
        // Path 2: Generate new PDF then poll for completion
        setPhase('generating');
        const currentCount = pdfExports.length;
        await surveyReportApi.generatePdfExport(reportId);
        if (isMountedRef.current) {
          startPolling(currentCount);
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        clearPolling();
        setPhase('error');
        setError(err instanceof Error ? err.message : 'PDF export failed. Please try again.');
      }
    }
  }, [phase, pdfExports, reportId, downloadExport, startPolling, clearPolling]);

  const isExporting = phase !== 'idle' && phase !== 'error';

  return { handleExport, isExporting, phase, error };
}

function getMostRecentExport(exports: PdfExportData[]): PdfExportData | undefined {
  if (exports.length === 0) return undefined;
  return [...exports].sort(
    (a, b) => new Date(b.exportedAt).getTime() - new Date(a.exportedAt).getTime()
  )[0];
}
